import React, { Fragment, useCallback, useEffect, useMemo } from "react";

import * as Yup from "yup";
import { Box, Typography, makeStyles } from "@material-ui/core";

import { default as BaseModal } from "@ds-developer/modal";
import Button from "@ds-developer/button";
import CurrencyField from "@ds-developer/currency-field";

import {
  UpdateHourlyRateFormFields,
  UpdateHourlyRateModalProps,
} from "./types";
import { formatCurrency } from "@ds-developer/frontend";
import { useForm } from "react-hook-form/dist/react-hook-form.ie11";

const useStyles = makeStyles(theme => ({
  box: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  actions: {
    justifyContent: "space-between",
    padding: theme.spacing(2),
    [theme.breakpoints.up("md")]: {
      padding: theme.spacing(2, 4),
    },
  },
}));

const UpdateHourlyRateModal: React.FC<UpdateHourlyRateModalProps> = ({
  fullName,
  maximumRate,
  loading = false,
  currentRate = 0,
  onSubmit = () => {},
  onCancel = () => {},
  handleClickHourlyRate,
}) => {
  const classes = useStyles();

  const defaultValues = { hourlyRate: currentRate };

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        hourlyRate: Yup.number()
          .typeError("Please enter a number")
          .required("Please enter a rate")
          .min(0, "Rate must be greater than zero")
          .max(maximumRate, "You can’t charge more than your region rate.")
          .test(
            "Is25pIncrement",
            "Hourly rate needs to be in £0.25 increments",
            val => !!val && val % 0.25 === 0,
          ),
      }),
    [maximumRate],
  );

  const {
    formState: { isValid },
    handleSubmit,
    register,
    errors,
    triggerValidation,
  } = useForm<UpdateHourlyRateFormFields>({
    defaultValues,
    mode: "onChange",
    validationSchema,
  });

  const Wrapper = useCallback<React.FC>(
    ({ children }) => <form onSubmit={handleSubmit(submit)}>{children}</form>,
    [handleSubmit],
  );

  useEffect(() => {
    /* 
    This is a terrible hack, but it's only needed so that Storybook shows the invalid state
    The initial values must be loaded async or something, because the initial render value is undefined
    */
    setTimeout(() => {
      triggerValidation("hourlyRate");
    }, 100);
  }, []);

  const submit = (values: UpdateHourlyRateFormFields) => {
    onSubmit(values.hourlyRate);
  };

  const actions = (
    <Fragment>
      <Button variant="outlined" onClick={onCancel}>
        Cancel
      </Button>
      <Button
        type="submit"
        variant="contained"
        color="primary"
        loading={loading}
        disabled={!isValid || loading}
      >
        Save
      </Button>
    </Fragment>
  );

  return (
    <BaseModal
      actions={actions}
      open
      title={`Edit hourly rate for ${fullName}`}
      wrapper={Wrapper}
    >
      <Box className={classes.box}>
        <Typography variant="body2" color="textSecondary">
          {`Current rate: ${formatCurrency(currentRate)}/hr`}
        </Typography>
      </Box>
      <Box className={classes.box}>
        <CurrencyField
          fullWidth
          inputRef={register}
          name="hourlyRate"
          id="hourlyRate"
          label="New hourly rate"
          variant="outlined"
          helperText={errors?.hourlyRate?.message}
          error={Boolean(errors?.hourlyRate)}
          min="0"
          onClick={handleClickHourlyRate}
        />
      </Box>
    </BaseModal>
  );
};

export default UpdateHourlyRateModal;
